import {
  anonymousTokenStorage,
  ApiError,
  createApiClient,
  isActiveContextForbiddenError,
  isTeamMemberDisabledError,
  type ApiClient,
  type ApiErrorContext,
  type TokenStorage,
} from '@kiao-voice/api-client'
import { getWebApiOrigin } from './api'
import { webClientInfoHeaders } from './web-client-info'
// Import the token primitives from token-storage (NOT auth-storage) to avoid an
// import cycle: auth-storage's fetchMe goes through this client.
import { clearStoredToken, readStoredToken, storeToken } from '../features/auth/token-storage'
import { emitMemberAccessRefresh } from './member-access-refresh'
import { emitWorkspaceRecovery } from './workspace-recovery'

/**
 * The single web API client (#223). Every authed web request goes through this
 * so token injection, the `X-Active-Context` header, base-url handling, and
 * auth self-heal live in ONE place instead of being re-implemented in each
 * feature's hand-rolled fetch wrapper.
 *
 * Self-heal centralised here (mirrors what the per-module wrappers used to do
 * individually). In every case the error STILL throws after the heal, so
 * callers keep classifying the thrown error (retry-on-recoverable-context vs
 * sign-out) exactly as before:
 *   - 403 `active_context_forbidden` → `clearActiveContext()` (handled INSIDE
 *     `createApiClient`; listed here for completeness).
 *   - 400 `invalid_active_context` → `clearActiveContext()` (the batch wrapper
 *     did this; centralised so every caller recovers from a provably-dead
 *     context).
 *   - 401 → `clearStoredToken()` (dead/expired bearer), guarded by
 *     `requestToken !== null && readStoredToken() === requestToken`: the
 *     non-null clause keeps a token-LESS 401 from evicting anything (and from
 *     feeding a clear→probe→401 loop), and the equality clause keeps a stale
 *     in-flight 401 carrying an old token from evicting a token a parallel
 *     sign-in just stored. `clearStoredToken` silently clears the active
 *     context (no subscriber notification) and fires AUTH_TOKEN_CHANGE_EVENT;
 *     AuthGuard reacts by rendering its sign-in placeholder — it only
 *     auto-OPENS the modal for a visitor who ARRIVED unauthenticated, never on
 *     a became-unauthenticated transition (see auth-guard.tsx).
 *   - 403 `team_member_disabled` → keeps the token and the context and asks
 *     the app shell to re-check access (`emitMemberAccessRefresh`), which then
 *     replaces the page with the disabled-workspace notice.
 *
 * Public (unauthenticated) reads still omit `X-Active-Context`: the active
 * context is unset for a signed-out visitor, so `createApiClient` sends no
 * header and the server applies its personal fallback.
 */
const tokenStorage: TokenStorage = {
  async getAccessToken() {
    return readStoredToken()
  },
  async setAccessToken(token: string) {
    storeToken(token)
  },
  async clearTokens() {
    clearStoredToken()
  },
}

function isInvalidActiveContext(error: ApiError): boolean {
  return (
    error.status === 400 &&
    (error.body as { error?: string } | null | undefined)?.error === 'invalid_active_context'
  )
}

function handleResponseError(
  error: ApiError,
  { requestToken, requestActiveContext, didClearActiveContext }: ApiErrorContext
): void {
  if (error.status === 401) {
    // Race guard: only evict if the token that 401'd is STILL the stored one
    // — and only when there WAS a token. `requestToken === null` means the
    // request was already tokenless (a signed-out visitor); `null ===
    // readStoredToken()` would also be true in that case, but there is
    // nothing to evict — calling `clearStoredToken()` anyway would still fire
    // `AUTH_TOKEN_CHANGE_EVENT` (it does so unconditionally), which could wake
    // a listener (e.g. `AuthGuard`) into re-checking and re-requesting with
    // still no token, forming a structural loop with no token ever present to
    // break it. Excluding `requestToken === null` closes that off entirely.
    if (requestToken !== null && readStoredToken() === requestToken) {
      clearStoredToken()
    }
    return
  }
  if (isTeamMemberDisabledError(error)) {
    emitMemberAccessRefresh(requestActiveContext)
    return
  }
  if (
    didClearActiveContext &&
    requestActiveContext !== null &&
    (isInvalidActiveContext(error) || isActiveContextForbiddenError(error))
  ) {
    emitWorkspaceRecovery(requestActiveContext)
  }
}

/**
 * Build a web API client. Pass `fetchFn` to inject a mock in tests; production
 * callers use the shared singleton via {@link getWebApiClient}.
 */
export function createWebApiClient(fetchFn?: typeof fetch): ApiClient {
  return createApiClient({
    baseUrl: getWebApiOrigin(),
    getDefaultHeaders: webClientInfoHeaders,
    tokenStorage,
    onResponseError: handleResponseError,
    fetchFn,
  })
}

let sharedClient: ApiClient | undefined

/** The shared singleton web API client. */
export function getWebApiClient(): ApiClient {
  sharedClient ??= createWebApiClient()
  return sharedClient
}

/**
 * A client for PUBLIC (anonymous) endpoints. Sends NO bearer token and NO
 * `X-Active-Context` — even when the visitor happens to be signed in — so an
 * anonymous endpoint (landing demo, guest checkout, invite preview, magic
 * login, anonymous trial) receives exactly what an unauthenticated caller would.
 * No auth self-heal either: a public request must never touch the user's
 * session, which is why `anonymousTokenStorage`'s writes are no-ops rather than
 * this client sharing the real `tokenStorage`.
 */
export function createWebPublicApiClient(fetchFn?: typeof fetch): ApiClient {
  return createApiClient({
    baseUrl: getWebApiOrigin(),
    getDefaultHeaders: webClientInfoHeaders,
    tokenStorage: anonymousTokenStorage,
    sendActiveContext: false,
    fetchFn,
  })
}

let sharedPublicClient: ApiClient | undefined

/** The shared singleton PUBLIC web API client (no auth, no active context). */
export function getWebPublicApiClient(): ApiClient {
  sharedPublicClient ??= createWebPublicApiClient()
  return sharedPublicClient
}

/**
 * An AUTHED client that sends NO `X-Active-Context`. For internal/admin
 * endpoints (`/internal/**`): they are gated on internal-org membership, not
 * the acting workspace, so the header is irrelevant — and a stale stored
 * context would otherwise 403 (`active_context_forbidden`) before the request
 * ever reached the internal gate, which the admin UI would misread as "not
 * internal staff". Same token storage + 401 self-heal as the main client.
 */
export function createWebAuthedNoContextClient(fetchFn?: typeof fetch): ApiClient {
  return createApiClient({
    baseUrl: getWebApiOrigin(),
    getDefaultHeaders: webClientInfoHeaders,
    tokenStorage,
    onResponseError: handleResponseError,
    sendActiveContext: false,
    fetchFn,
  })
}

let sharedAuthedNoContextClient: ApiClient | undefined

/** The shared singleton authed no-context client (internal/admin surfaces). */
export function getWebAuthedNoContextClient(): ApiClient {
  sharedAuthedNoContextClient ??= createWebAuthedNoContextClient()
  return sharedAuthedNoContextClient
}
